/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faCircleXmark, faPlay, faPlus } from "@fortawesome/free-solid-svg-icons";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation } from "react-router-dom";
import LocationCard from "../settings/myProfile/LocationCard";
import useAxios from "../../hooks/useAxios";
import { API_ENDPOINTS } from "../../constants/api";
import XSelect from "../../component/form/XSelect";
import { userStatusOptions } from "../../utils/options";
import { toaster } from "../../utils/toast";
import useSidebar from "../../hooks/useSidebar";
import XButton from "../../component/button/XButton";
import PhoneNumberInput from "../../component/phoneInput/phoneInput";
import XInput from "../../component/form/XInput"; // adjust to your actual relative path, matching EditCompany.tsx's import
import useLocationData from "../../hooks/useLocationData";

const MAX_BUSINESS_LOGO_SIZE_BYTES = 1024 * 1024;

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  firstName: Yup.string().required("First Name is required").matches(/^[A-Za-z\s]+$/, "First Name must contain only alphabets"),
  lastName: Yup.string().required("Last Name is required").matches(/^[A-Za-z\s]+$/, "Last Name must contain only alphabets"),
  organizationName: Yup.string().required("Organization name is required"),
  // businessLogo: Yup.string().required("Logo is required"),
  websiteURL: Yup.string().required("URL is required").url("Enter a valid website URL"),
  status: Yup.string().required("Status is required"),
  address: Yup.string().required("Address is required"),
  zipcode: Yup.string()
    .required("Zipcode is required")
    .matches(/^\d{5}$/, "Zipcode must be a number of 5 digits"),
  country_id: Yup.string().required("Country is required"),
  state_id: Yup.string().required("State is required"),
  city: Yup.string().required("City is required"),
  locations: Yup.array()
    .of(
      Yup.object().shape({
        display_name: Yup.string().trim().required("Location Name is required"),
        address: Yup.string().trim().required("Address is required"),
        zipcode: Yup.string()
          .required("Zipcode is required")
          .matches(/^\d{5}$/, "Zipcode must be a number of 5 digits"),
        reply_to_email: Yup.string().trim().email("Enter a valid email address").required("Reply-to Email is required"),
        from_name: Yup.string().trim().required("From Email Name is required"),
        location_timezone: Yup.string().required("Timezone is required"),
        country_id: Yup.string().required("country is required"),
        state_id: Yup.string().required("state is required"),
        city: Yup.string().trim().required("City is required"),
        rss_feeds: Yup.array().of(Yup.object().shape({
          feed_name: Yup.string().trim().required("RSS Feed Name is required"),
          feed_url: Yup.string().trim().url("Enter a valid RSS Feed URL").required("RSS Feed URL is required"),
          max_items: Yup.number()
            .transform((value, originalValue) => originalValue === "" ? null : value)
            .nullable()
            .integer("Must be a whole number")
            .min(1, "Must be at least 1")
            .max(10, "Must be 10 or fewer"),
        })),
      })
    )
    .min(1, "At least one location is required"),
});

const AddOrganization = () => {
  const navigate = useNavigate();
  const { postData, isLoading } = useAxios();
  const location = useLocation();
  const { selectedOrgData } = location.state || {};
  const { onShowSidebar } = useSidebar();
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [isBusinessLogoRemoved, setIsBusinessLogoRemoved] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const { countries, states, fetchStates } = useLocationData();

  const formik = useFormik({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
      organizationName: "",
      businessLogo: "",
      websiteURL: "",
      phoneNumber: "",
      totalCountPerCompany: "",
      contactsMinusUnsubscribes: "",
      status: "",
      is_2fa: "Active",
      address: "",
      zipcode: "",
      country_id: "",
      state_id: "",
      city: "",
      locations: [
        {
          display_name: "",
          country_id: "",
          state_id: "",
          city: "",
          address: "",
          zipcode: "",
          reply_to_email: "",
          from_name: "",
          location_timezone: "UTC",
          is_active: true,
          rss_feeds: [],
        },
      ],
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const selectedBusinessLogo: any = values.businessLogo;
      if (typeof File !== "undefined" && selectedBusinessLogo instanceof File && selectedBusinessLogo.size > MAX_BUSINESS_LOGO_SIZE_BYTES) {
        toaster({ message: "Business logo exceeds 1 MB. Please choose a business logo no larger than 1 MB.", success: false });
        return;
      }
      const oversizedLocation = values.locations.find((location: any) => location.logoFile instanceof File && location.logoFile.size > MAX_BUSINESS_LOGO_SIZE_BYTES);
      if (oversizedLocation) {
        toaster({ message: "Location logo exceeds 1 MB. Please choose a location logo no larger than 1 MB.", success: false });
        return;
      }

      let preLogo = selectedOrgData?.filename;
      const payload = {
        email: values.email,
        first_name: values.firstName,
        last_name: values.lastName,
        business_name: values.organizationName,
        previousLogo: preLogo,
        website_url: values.websiteURL,
        phone_number: values.phoneNumber,
        is_active: values.status === "Active" ? true : false,
        is_2fa: values.is_2fa === "Deactive" ? false : true,
        removeLogo: Boolean(isBusinessLogoRemoved),
        locations: values.locations,
        address: values.address,
        zipcode: values.zipcode,
        country_id: values.country_id,
        state_id: values.state_id,
        city: values.city,
      };
      let response
      try {
        if (selectedOrgData === undefined) {
          const createPayload = {
            ...payload,
            locations: values.locations.map(({ logoFile, removeLogo, ...location }: any) => location),
          };
          const formData = new FormData();
          formData.append("body", JSON.stringify(createPayload));
          if (selectedBusinessLogo instanceof File) {
            formData.append("logo", selectedBusinessLogo);
          }
          values.locations.forEach((location: any, index: number) => {
            if (location.logoFile instanceof File) {
              formData.append(`locationLogo_${index}`, location.logoFile);
            }
          });
          response = await postData(API_ENDPOINTS.ADD_UPDATE_ORGANIZATION, formData);
        } else {
          const updatedPayload = { ...payload, orgId: selectedOrgData.userId };
          updatedPayload.locations = values.locations.map(({ logoFile, removeLogo, ...location }: any) => ({
            ...location,
            removeLogo: Boolean(removeLogo),
          }));
          const formData = new FormData();
          formData.append("body", JSON.stringify(updatedPayload));
          if (selectedBusinessLogo instanceof File) {
            formData.append("logo", selectedBusinessLogo);
          }
          values.locations.forEach((location: any, index: number) => {
            if (location.logoFile instanceof File) {
              formData.append(`locationLogo_${index}`, location.logoFile);
            }
          });
          response = await postData(API_ENDPOINTS.ADD_UPDATE_ORGANIZATION, formData);
        }
        const {success, message} = response;
        if (success) {
          navigate("/Admin/Organizations");
          formik.resetForm();
        }
        toaster({ message, success });
      } catch (error) {
        toaster({ success: false, message: "Something went wrong" });
      }
    },
  });
  useEffect(() => {
    if (selectedOrgData) {
      formik.setValues({
        ...formik.values,
        email: selectedOrgData.email,
        firstName: selectedOrgData.first_name,
        lastName: selectedOrgData.last_name,
        organizationName: selectedOrgData.business_name,
        businessLogo: selectedOrgData.filename,
        websiteURL: selectedOrgData.website_url,
        phoneNumber: selectedOrgData.phone_number,
        status: selectedOrgData.is_active ? "Active" : "Deactive",
        is_2fa: selectedOrgData.is_2fa ? "Active" : "Deactive",
        locations: (selectedOrgData.locations ?? formik.values.locations).map((location: any) => ({
          ...location,
          location_id: location.location_id ?? location.id,
          display_name: location.display_name ?? "",
          reply_to_email: location.reply_to_email ?? "",
          from_name: location.from_name ?? "",
          location_timezone: location.location_timezone || "UTC",
          logoFile: null,
          removeLogo: false,
          rss_feeds: location.rss_feeds ?? (location.rss_feed_url ? [{
            feed_name: "RSS Feed",
            feed_url: location.rss_feed_url,
            max_items: location.rss_max_items ?? "",
          }] : []),
        })),
        address: selectedOrgData.address ?? "",
        zipcode: selectedOrgData.zipcode ?? "",
        country_id: selectedOrgData.country_id ?? "",
        state_id: selectedOrgData.state_id ?? "",
        city: selectedOrgData.city ?? "",
      });
    }
  }, [selectedOrgData, formik.setValues]);

    
    useEffect(() => {
    if (formik.values.country_id) {
      fetchStates(formik.values.country_id);
    }
  }, [formik.values.country_id]);

  const addLocationHandler = () => {
    const blankLocationObject = [
      {
        display_name: "",
        country_id: "",
        state_id: "",
        city: "",
        address: "",
        zipcode: "",
        reply_to_email: "",
        from_name: "",
        location_timezone: "UTC",
        rss_feeds: [],
      },
    ];
    formik.setFieldValue("locations", [
      ...formik.values.locations,
      ...blankLocationObject,
    ]);
  };

  const removeLocationHandler = (index: number) => {
    const newLocations = [...formik.values.locations];
    if (newLocations.length > 1) {
      newLocations.splice(index, 1);
      formik.setFieldValue("locations", newLocations);
    }
  };

  const handleImageChange = (selectedImage: any) => {
    if (selectedImage) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result as string);
      };
      reader.readAsDataURL(selectedImage);
    }
  };

  return (
    <div>
      <div className="main-title ">
        <h2>
          <span className="d-lg-none cursor-pointer" onClick={()=>onShowSidebar()}>
            <FontAwesomeIcon icon={faBars} />
          </span>
          <p><span onClick={() => navigate("/Admin/Organizations")}><FontAwesomeIcon icon={faPlay} />{selectedOrgData ? "Edit Organization" : "Add Organization"}</span></p>
        </h2>
      </div>
      <div className="add-contact">
        <Form onSubmit={formik.handleSubmit}>
          <Row>
            <Col xl={4} md={6} xs={12}>
              <Form.Group className="form-group" controlId="formBasicEmail">
                <Form.Label>Email Address</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="Enter"
                  disabled={Boolean(selectedOrgData && selectedOrgData.is_registration_complete)}
                  title={selectedOrgData && selectedOrgData.is_registration_complete ? "Email cannot be changed once registration is complete" : undefined}
                  {...formik.getFieldProps("email")}
                />
                <Form.Text className="text-danger error-text">
                  {formik.touched.email && formik.errors.email}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col xl={4} md={6} xs={12}>
              <Form.Group className="form-group" controlId="formBasicEmail">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter"
                  {...formik.getFieldProps("firstName")}
                />
                <Form.Text className="text-danger error-text">
                  {formik.touched.firstName && formik.errors.firstName}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col xl={4} md={6} xs={12}>
              <Form.Group className="form-group" controlId="formBasicEmail">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter"
                  {...formik.getFieldProps("lastName")}
                />
                <Form.Text className="text-danger error-text">
                  {formik.touched.lastName && formik.errors.lastName}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col xl={4} md={6} xs={12}>
              <Form.Group className="form-group" controlId="formBasicEmail">
                <Form.Label>Organization Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter"
                  {...formik.getFieldProps("organizationName")}
                />
                <Form.Text className="text-danger error-text">
                  {formik.touched.organizationName &&
                    formik.errors.organizationName}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col xl={4} md={6} xs={12}>
              <Form.Group
                controlId="formBasicEmail"
                className="position-relative form-group"
              >
                <Form.Label>Upload Business Logo</Form.Label>
                <div className='position-relative'>
                  <Form.Control
                    ref={logoInputRef}
                    type="file"
                    name="businessLogo"
                    id="admin-business-logo-file"
                    accept='image/*'
                    className='visually-hidden'
                    onChange={(e) => {
                      const file = (e.currentTarget as HTMLInputElement).files?.[0];
                      if (!file) return;
                      if (file.size > MAX_BUSINESS_LOGO_SIZE_BYTES) {
                        toaster({ message: "Business logo exceeds 1 MB. Please choose a business logo no larger than 1 MB.", success: false });
                        e.currentTarget.value = '';
                        return;
                          }
                          handleImageChange(file);
                          formik.setFieldValue("businessLogo", file);
                          setIsBusinessLogoRemoved(false);
                        }}
                      />
                  <label htmlFor='admin-business-logo-file' className='logo-dropzone'>
                    <svg className='logo-dropzone-icon' viewBox='0 0 24 24' aria-hidden='true'>
                      <path d='M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14.5v3A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-3' />
                    </svg>
                    <span className='logo-dropzone-action'>{previewImage || (!isBusinessLogoRemoved && selectedOrgData?.logoUrl) ? 'Edit Logo' : 'Upload logo'}</span>
                    {(previewImage || (!isBusinessLogoRemoved && selectedOrgData?.logoUrl)) && (
                      <img className='logo-dropzone-preview' alt='Business logo preview' src={previewImage || selectedOrgData?.logoUrl} />
                    )}
                  </label>
                  {(previewImage || (!isBusinessLogoRemoved && selectedOrgData?.logoUrl)) && (
                    <button
                      type="button"
                      className="logo-dropzone-remove"
                      aria-label="Remove business logo"
                      title="Remove Logo"
                      onClick={() => {
                        setPreviewImage(null);
                        setIsBusinessLogoRemoved(true);
                        formik.setFieldValue("businessLogo", null);
                        if (logoInputRef.current) logoInputRef.current.value = "";
                      }}
                    >
                      <FontAwesomeIcon icon={faCircleXmark} />
                    </button>
                  )}
                </div>
                {/* <FontAwesomeIcon icon={faUpload} className='upload-icon' /> */}
                <Form.Text className="text-danger error-text">
                  {formik.touched.businessLogo && formik.errors.businessLogo}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col xl={4} md={6} xs={12}>
              <Form.Group className="form-group" controlId="formBasicEmail">
                <Form.Label>Website URL</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="https://www.website-url.com"
                  {...formik.getFieldProps("websiteURL")}
                />
                <Form.Text className="text-danger error-text">
                  {formik.touched.websiteURL && formik.errors.websiteURL}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col xl={4} md={6} xs={12}>
              <PhoneNumberInput
                value={formik.values.phoneNumber}
                onChange={phone => formik.setFieldValue('phoneNumber', phone)}
                error={formik.errors.phoneNumber}
                touched={formik.touched.phoneNumber}
              />
            </Col>
            <Col xl={4} md={6} xs={12}>
            <XInput name="address" label="Address" formik={formik} />
          </Col>
          <Col xl={4} md={6} xs={12}>
            <XSelect
              name="country_id"
              label="Country"
              options={Object.values(countries)}
              formik={formik}
              onChange={(e: any) => {
                formik.handleChange(e);
                formik.setFieldValue("state_id", "");
              }}
            />
          </Col>
          <Col xl={4} md={6} xs={12}>
            <XSelect
              name="state_id"
              label="State"
              options={Object.values(states)}
              formik={formik}
            />
          </Col>
          <Col xl={4} md={6} xs={12}>
            <XInput name="city" label="City" formik={formik} />
          </Col>
          <Col xl={4} md={6} xs={12}>
            <XInput name="zipcode" label="ZIP Code" formik={formik} />
          </Col>
            <Col>
              <Form.Group className="form-group" controlId="formBasicEmail">
                <XSelect
                  name={`status`}
                  label="Status"
                  options={userStatusOptions}
                  formik={formik}
                />
              </Form.Group>
            </Col>
            {selectedOrgData && (
              <Col>
                <Form.Group className="form-group" controlId="form2FAStatus">
                  <XSelect
                    name={`is_2fa`}
                    label="Two Factor Authentication"
                    options={userStatusOptions}
                    formik={formik}
                  />
                </Form.Group>
              </Col>
            )}
          </Row>
          <p className="text-end add-link" >
            <span className='btn-add' onClick={addLocationHandler}><FontAwesomeIcon icon={faPlus} /> <span>Add Location</span></span>
          </p>
          {formik.values.locations.map((v: any, index: number) => (
            <LocationCard
              key={index}
              index={index}
              locationData={v}
              formik={formik}
              removeLocationHandler={removeLocationHandler}
              canManageLocations={true}
              canEditRss={true}
            />
          ))}
          <div className="btns">
            <div className="d-flex justify-content-between align-items-center">
              <Button
                variant="primary"
                className="btn-theme btn-border"
                onClick={() => navigate("/Admin/Organizations")}
              >
                <FontAwesomeIcon icon={faPlay} />
                Back
              </Button>
              <XButton
                variant="primary"
                type="submit"
                buttonText={selectedOrgData ?(<> Save Changes <FontAwesomeIcon icon={faPlay} /></> ) : "Submit"}
                loading={isLoading}
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AddOrganization;
